import { isEmptyTextWrap } from 'common/Prose/utils/isEmptyTextWrap'
import { Node as ProseNode } from 'prosemirror-model'
import { distributeEvenly } from 'utils/arrayUtils'
import { splitNodeIntoSentences } from './splitNodeIntoSentences'

/**
 * Splits an array of `ProseNode`s into chunks, where each chunk contains a roughly equal number of
 * nodes, balanced as evenly as possible. Empty `TextWrap`s do not count when distributing, and will
 * be placed as close to the end of their chunk as possible (thus generating a visible bias where
 * chunks tend to end in empty lines and start with non-empty lines)
 * @param sourceArray The array containing the `ProseNode`s to split into chunks
 * @param numChunks The number of chunks to split the array into
 * @returns An array of arrays, `numChunks` elements long, where each array contains the `ProseNode`s
 * that should be in that chunk
 * @example
 * ```typescript
 * const sourceArray = [p("lorem"), p("ipsum"), p(), p("dolor"), p("sit"), p("amet"), p()]
 * const chunked = splitNodeArrayIntoChunks(sourceArray, 3)
 * // chunked = [
 * //   [p("lorem"), p("ipsum"), p()],
 * //   [p("dolor"), p("sit")],
 * //   [p("amet"), p()],
 * // ]
 * ```
 */
export function splitNodeArrayIntoChunks(sourceArray: ProseNode[], numChunks: number): ProseNode[][] {
  const chunks: ProseNode[][] = Array(numChunks)
    .fill(undefined)
    .map(() => [])
  let nonEmptyTextWraps = sourceArray.filter((n) => !isEmptyTextWrap(n))

  if (nonEmptyTextWraps.length === 1) {
    // If the source array only contains one non-empty TextWrap, we can't split it into chunks
    // so instead we will "split by sentence", that is, split that node into multiple chunks of the
    // same type, using a period as the delimiter, and then distribute those chunks evenly
    // we should take care to preserve marks and attributes when splitting
    const node = nonEmptyTextWraps[0]
    sourceArray = splitNodeIntoSentences(node, numChunks)
    nonEmptyTextWraps = sourceArray.filter((n) => !isEmptyTextWrap(n))
  }

  const desiredChunkSizeMap = distributeEvenly(nonEmptyTextWraps.length, numChunks)
  const currentChunkSizeMap = Array(numChunks).fill(0)
  let currentChunkIdx = 0

  sourceArray.forEach((node) => {
    if (isEmptyTextWrap(node)) {
      const targetChunkIdx = (() => {
        if (currentChunkIdx >= chunks.length || chunks[currentChunkIdx].length === 0) {
          return Math.max(0, currentChunkIdx - 1)
        }
        return currentChunkIdx
      })()
      chunks[targetChunkIdx].push(node)
    } else {
      chunks[currentChunkIdx].push(node)
      currentChunkSizeMap[currentChunkIdx] += 1
      if (currentChunkSizeMap[currentChunkIdx] >= desiredChunkSizeMap[currentChunkIdx]) {
        currentChunkIdx += 1
      }
    }
  })
  return chunks
}
